import { Helmet } from "react-helmet-async";
import { brand } from "@/data/site/brand";
import { truncateDescription } from "@/lib/seoText";


export interface PageSEOBreadcrumb {
  name: string;
  href: string;
}

interface PageSEOProps {
  title: string;
  description: string;
  path: string;
  noindex?: boolean;
  jsonLd?: object | object[];
  /** Absolute or root-relative path to an OG/Twitter share image. */
  image?: string;
  /** Open Graph type — defaults to "website". Use "article" for blog posts. */
  ogType?: "website" | "article" | "profile";
  /** Breadcrumb trail — auto-emits BreadcrumbList JSON-LD when provided. */
  breadcrumbs?: PageSEOBreadcrumb[];
  /** ISO date (YYYY-MM-DD) for published/modified content meta. */
  publishedTime?: string;
  modifiedTime?: string;
  /** Author byline for article-type pages. */
  author?: string;
  /** CSS selectors marking speakable answer regions for voice search.
   *  Emits SpeakableSpecification JSON-LD so Assistant-class crawlers
   *  know which sentences to read aloud. */
  speakableSelectors?: string[];
}

const DEFAULT_OG_IMAGE = "/og-default.jpg";

/**
 * Per-page SEO for organic, indexable pages.
 * Sets title, description, canonical, OG/Twitter, robots, and optional JSON-LD
 * (including automatic BreadcrumbList from `breadcrumbs`).
 */
export const PageSEO = ({
  title,
  description,
  path,
  noindex,
  jsonLd,
  image,
  ogType = "website",
  breadcrumbs,
  publishedTime,
  modifiedTime,
  author,
  speakableSelectors,
}: PageSEOProps) => {
  const canonical = `${brand.siteUrl}${path}`;
  // Keep the final <title> at ≤60 chars for SERP display. Use the full brand
  // suffix when it fits, otherwise fall back to the short "CTR" acronym so
  // long service titles don't get truncated in search results.
  const fullSuffix = ` | ${brand.shortName}`;
  const shortSuffix = " | CTR";
  const fullTitle = title.includes(brand.shortName) || title.includes("CTR")
    ? title
    : title.length + fullSuffix.length <= 60
      ? title + fullSuffix
      : title + shortSuffix;
  const resolvedImage = image
    ? image.startsWith("http")
      ? image
      : `${brand.siteUrl}${image}`
    : `${brand.siteUrl}${DEFAULT_OG_IMAGE}`;

  const schemas: object[] = [];
  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: b.href.startsWith("http") ? b.href : `${brand.siteUrl}${b.href}`,
      })),
    });
  }
  if (speakableSelectors && speakableSelectors.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${canonical}#webpage`,
      url: canonical,
      name: fullTitle,
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: speakableSelectors,
      },
    });
  }
  if (jsonLd) {
    if (Array.isArray(jsonLd)) schemas.push(...jsonLd);
    else schemas.push(jsonLd);
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={truncateDescription(description)} />

      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={
          noindex
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        }
      />
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={truncateDescription(description)} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={brand.legalName} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image" content={resolvedImage} />
      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {author && <meta property="article:author" content={author} />}
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={truncateDescription(description)} />
      <meta name="twitter:image" content={resolvedImage} />
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};
